import React, { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../index";
import { Card, Button, Spinner } from "react-bootstrap";
import { getDashboardActivityApi } from "../../http/userLogAPI";

const activityCache = new Map();
const FRESH_MS = 15 * 60 * 1000;
const DISPLAY_LIMIT = 80;
const isTechnicalResourceError = (message = "") =>
    /out of shared memory|max_locks_per_transaction|could not resize shared memory/i.test(String(message));

// 3-словные фразы, Title Case
const TYPE_MINI = {
    success_confirm:              'Review Confirmed Successfully',
    reported:                     'Review Report Submitted',
    review_link_received:         'Review Link Received',

    assignment_create:            'Assignment Created Successfully',
    take_assign:                     'Assignment Taken Successfully',
    assign_taken:                    'Assignment Started Successfully',
    owner_cancel_assign:             'Assignment Cancelled by Owner',
    assignment_cancelled_by_support: 'Assignment Cancelled by Support',

    book_updated:                    'Book/Product Updated Successfully',
    book_status_live:                'Book/Product Approved and Now Live',
    complaint_received:              'Complaint Received and Logged',

    pool_created:                    'Poll Created Successfully',
    pool_updated:                    'Poll Updated Successfully',
    payment_successful:           'Payment Processed Successfully',
    payment_success:              'Payment Processed Successfully',
    change_tariff:                'Plan Changed Successfully',
    add_funds:                    'Balance Topped Up',
    withdrawal_requested:         'Payout Request Submitted',

    rebate:                       'Rebate Credit Issued',
    penalty:                      'Penalty Charge Applied',
    paper_reward:                 'Paper Review Payout',
    reward_poll:                  'Poll Reward Paid',

    take_poll:                    'Poll Voting Started',
    cancel_expired_pool_item:     'Poll Assignment Expired',

    profile:                      'Profile Changes Saved',
    fraud_flag:                   'Fraud Alert Detected',
    transfer:                     'Transfer Operation Completed',
    welcome:                      'Welcome Bonus Granted',
    referral:                     'Referral Bonus Granted',
};

const typeKey = (t) => String(t || '').toLowerCase().replace(/\s+/g, '_');

const shortMyText = (log) => {
    const key = typeKey(log?.log_type || log?.event_type);
    // если сервер уже прислал `text: "User X: ..."`, уберём префикс и возьмём хвост после «: »
    const fromServer = typeof log?.text === 'string'
        ? log.text.split(': ').slice(1).join(': ')
        : null;

    return TYPE_MINI[key]
        || fromServer
        || log?.title
        || log?.description
        || 'New Customer Joined to Virtalibry';
};

const getCreatedAt = (log) => log?.createdAt ?? log?.user_log_date ?? null;

/**
 * DashboardActivity — 3‑колоночный предпросмотр данных дашборда:
 * 1) recent — свежая активность во всей системе
 * 2) topWeek — топ пользователей за 7 дней (published + taken)
 * 3) my — логи текущего пользователя
 *
 * Стиль и UX следуют вашим паттернам из VirtUserLogsInDashboard / TransferVirt… / SellVirtOnStock:
 * - Card с компактными заголовками
 * - responsive: мобильные карточки, на десктопе таблицы
 * - раскрытие строк по клику (openRows)
 * - кнопка Refresh + обработка загрузки/ошибок
 */
const DashboardActivity = () => {
    const { user } = useContext(Context);
    const cacheKey = String(user?.user?.id || 'guest');
    const cached = activityCache.get(cacheKey);

    // payload
    const [recent, setRecent] = useState(cached?.recent || []);
    const [topWeek, setTopWeek] = useState(cached?.topWeek || null);
    const [myLogs, setMyLogs] = useState(cached?.myLogs || []);
    const [meta, setMeta] = useState(cached?.meta || null);

    // ui
    const [loading, setLoading] = useState(!cached);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);


    // параметры запроса (как просил — передаём userId с фронта)
    const params = useMemo(() => ({
        limitRecent: 30,
        limitMy: 10,
        topLimit: 10,
        userId: user?.user?.id || undefined,
    }), [user?.user?.id]);

    const [displayed, setDisplayed] = useState([]);   // то, что рисуем (верх — новое)
    const [, setLiveQueue] = useState([]);   // свежие, ждут показа
    const [animatingId, setAnimatingId] = useState(null); // ид последней “вспышки”
    const [queueHasItems, setQueueHasItems] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [warningMessage, setWarningMessage] = useState(cached?.warningMessage || '');
    const [dismissedWarningMessage, setDismissedWarningMessage] = useState(() => {
        try {
            return localStorage.getItem(`dashboardWarningDismissed:${cacheKey}`) || '';
        } catch {
            return '';
        }
    });

    // NEW: leaderboards (7d)
    const [topQuality7d, setTopQuality7d] = useState(cached?.topQuality7d || null);
    const [topAffiliate7d, setTopAffiliate7d] = useState(cached?.topAffiliate7d || null);
    const [topBurn7d, setTopBurn7d] = useState(cached?.topBurn7d || null);

    // NEW: per-card tooltips
    const [showTooltipQuality, setShowTooltipQuality] = useState(false);
    const [showTooltipAffiliate, setShowTooltipAffiliate] = useState(false);
    const [showTooltipBurn, setShowTooltipBurn] = useState(false);

    const now = () => Date.now();

    const toMs = (d) => {
        if (!d) return null;
        if (typeof d === 'number') return (String(d).length <= 10 ? d * 1000 : d);
        const t = new Date(d).getTime();
        return Number.isFinite(t) ? t : null;
    };

    // при смене recent — пересинхроним
    useEffect(() => {
        if (!Array.isArray(recent)) return;

        // нормализуем и сортируем по времени (новые сверху)
        const normalized = recent
            .map((r) => ({
                ...r,
                _ts: toMs(r.createdAt) ?? toMs(r.user_log_date) ?? toMs(r.created_at) ?? toMs(r.timestamp) ?? 0,
            }))
            .filter(x => x._ts > 0)
            .sort((a, b) => b._ts - a._ts);

        const cutoff = now() - FRESH_MS;

        // уже показанные id
        const shownIds = new Set(displayed.map(x => x.id).filter(Boolean));

        const fresh = normalized.filter(x => x._ts >= cutoff && !shownIds.has(x.id));
        const stale = normalized.filter(x => x._ts < cutoff || shownIds.has(x.id));

        // в displayed кладём видимый список (без дублей), в liveQueue — только свежие, которых ещё не было
        setDisplayed((prev) => {
            // соберём все уникальные: свежие (но без дублей) + часть старых
            const byId = new Map();
            const push = (arr) => arr.forEach(item => {
                const key = item.id ?? `${item._ts}-${item.text}`;
                if (!byId.has(key)) byId.set(key, item);
            });

            push(prev);         // что уже было
            push(stale);        // старые с сервера
            // свежие пока не показываем — они пойдут в liveQueue
            const arr = Array.from(byId.values())
                .sort((a, b) => b._ts - a._ts)
                .slice(0, DISPLAY_LIMIT);

            return arr;
        });

        setLiveQueue((prev) => {
            // объединяем старую очередь + новые свежие, удаляя дубли
            const byId = new Map();
            const keyOf = (x) => x.id ?? `${x._ts}-${x.text}`;
            [...prev, ...fresh].forEach(x => byId.set(keyOf(x), x));
            const arr = Array.from(byId.values()).sort((a, b) => a._ts - b._ts); // из очереди будем вытаскивать по времени (старше — раньше)
            setQueueHasItems(arr.length > 0);
            return arr;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recent]);

    useEffect(() => {
        let timer;
        const schedule = () => {
            // случайный интервал 10–30 сек
            const ms = 10000 + Math.floor(Math.random() * 20000);
            timer = setTimeout(tick, ms);
        };

        const tick = () => {
            // не анимируем в неактивной вкладке
            if (document.hidden) { schedule(); return; }

            setLiveQueue((q) => {
                if (!q.length) { setQueueHasItems(false); schedule(); return q; }

                const cutoff = now() - FRESH_MS;
                // найдём первый элемент, который всё ещё свежий
                const idx = q.findIndex(x => x._ts >= cutoff);
                if (idx === -1) {
                    // всё устарело — очищаем очередь
                    setQueueHasItems(false);
                    return [];
                }

                const item = q[idx];
                const rest = q.filter((_, i) => i !== idx);

                // вставляем item в displayed сверху
                setDisplayed((prev) => {
                    // дедуп по id/ключу
                    const keyOf = (x) => x.id ?? `${x._ts}-${x.text}`;
                    const already = new Set(prev.map(keyOf));
                    if (already.has(keyOf(item))) {
                        // если уже есть — просто не вставляем
                        return prev;
                    }
                    const next = [item, ...prev].slice(0, DISPLAY_LIMIT);
                    // подсветим этот id
                    setAnimatingId(item.id ?? keyOf(item));
                    // снимем подсветку через 1.8с
                    setTimeout(() => setAnimatingId(null), 20000);
                    return next;
                });

                setQueueHasItems(rest.length > 0);
                // переназначаем следующий таймер
                schedule();
                return rest;
            });
        };

        schedule();
        return () => clearTimeout(timer);
    }, []);

    const load = async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true); else setLoading(true);
            setError(null);
            const data = await getDashboardActivityApi(params);
            const next = {
                recent: Array.isArray(data?.recent) ? data.recent : [],
                topWeek: data?.topWeek || null,
                myLogs: Array.isArray(data?.my) ? data.my : [],
                meta: data?.meta || null,
                warningMessage: data?.warning || '',
                topQuality7d: data?.topQuality7d || null,
                topAffiliate7d: data?.topAffiliate7d || null,
                topBurn7d: data?.topBurn7d || null,
            };
            activityCache.set(cacheKey, next);
            setRecent(next.recent);
            setTopWeek(next.topWeek);
            setMyLogs(next.myLogs);
            setMeta(next.meta);
            setWarningMessage(next.warningMessage);
            // NEW: three leaderboards
            setTopQuality7d(next.topQuality7d);
            setTopAffiliate7d(next.topAffiliate7d);
            setTopBurn7d(next.topBurn7d);
        } catch (e) {
            const rawMessage = e?.response?.data?.message || e?.message || "";
            setError(isTechnicalResourceError(rawMessage)
                ? "Live activity data is temporarily unavailable. Please refresh in a moment."
                : rawMessage || "Failed to load dashboard activity");
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    };

    useEffect(() => {
        const nextCached = activityCache.get(cacheKey);
        try {
            setDismissedWarningMessage(localStorage.getItem(`dashboardWarningDismissed:${cacheKey}`) || '');
        } catch {
            setDismissedWarningMessage('');
        }
        if (nextCached) {
            setRecent(nextCached.recent || []);
            setTopWeek(nextCached.topWeek || null);
            setMyLogs(nextCached.myLogs || []);
            setMeta(nextCached.meta || null);
            setWarningMessage(nextCached.warningMessage || '');
            setTopQuality7d(nextCached.topQuality7d || null);
            setTopAffiliate7d(nextCached.topAffiliate7d || null);
            setTopBurn7d(nextCached.topBurn7d || null);
            setLoading(false);
            return;
        }
        load(false);
        // eslint-disable-next-line
    }, [cacheKey]);

    const dismissWarning = () => {
        setDismissedWarningMessage(warningMessage);
        try {
            localStorage.setItem(`dashboardWarningDismissed:${cacheKey}`, warningMessage);
        } catch {
            // ignore unavailable storage
        }
    };

    const showWarningCard = Boolean(warningMessage) && warningMessage !== dismissedWarningMessage;

// НОВЫЙ форматтер: «N минут назад», «только что», «вчера», ...
    const fmtTimeAgo = (input) => {
        const toDate = (d) => {
            if (!d) return null;
            try {
                if (typeof d === 'number' && String(d).length <= 10) return new Date(d * 1000); // seconds -> ms
                const dt = new Date(d);
                return Number.isFinite(dt.getTime()) ? dt : null;
            } catch { return null; }
        };

        const dt = toDate(input);
        if (!dt) return '—';

        let s = Math.round((Date.now() - dt.getTime()) / 1000);
        if (s < 0) s = 0;

        if (s < 5) return 'now';
        if (s < 60) return `${s}s ago`;

        const m = Math.floor(s / 60);
        if (m < 60) return `${m}m ago`;

        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;

        const d = Math.floor(h / 24);
        if (d < 7) return `${d}d ago`;

        const w = Math.floor(d / 7);
        if (w < 5) return `${w}w ago`;

        const mo = Math.floor(d / 30);
        if (mo < 12) return `${mo}mo ago`;

        const y = Math.floor(d / 365);
        return `${y}y ago`;
    };


    return (<>

            {showWarningCard && (<>
                {loading ? (
                        <div className="text-center py-3 text-muted small">
                            <Spinner animation="border" size="sm" /> Loading dashboard…
                        </div>
                    ) : (<>
                <Card
                    role="alert"
                    aria-live="assertive"
                    className="w-100 mt-2 border-danger"
                >
                    <Card.Header className="py-1 d-flex align-items-center justify-content-between bg-danger bg-opacity-10 text-danger">
                        <span>Warning</span>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close warning"
                            onClick={dismissWarning}
                            style={{
                                width: "0.65rem",
                                height: "0.65rem",
                                padding: "0.2rem",
                                backgroundSize: "0.65rem",
                            }}
                        />
                    </Card.Header>
                    <Card.Body className="p-2 bg-danger bg-opacity-10">
                        <div className="small text-dark">
                            {warningMessage}
                        </div>
                    </Card.Body>
                </Card>
            </>)}
        </>)}
           <Card className="p-3 w-100 mt-2">
            <div className="d-flex flex-column flex-md-row flex-md-nowrap justify-content-between align-items-start align-items-md-center gap-2">
                <h5 className="text-muted mb-2 mb-md-0 me-md-3 text-nowrap">Live Activity & Leaders</h5>
                <div className="d-flex flex-column flex-md-row flex-md-nowrap justify-content-between align-items-start align-items-md-center gap-2">
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={refreshing}
                        onClick={() => load(true)}
                    >
                        {refreshing ? 'Refreshing Data, One Sec…' : 'Refresh Activity Data'}
                    </Button>
                </div>

            </div>

            {error && (
                <div className="alert alert-danger small mt-2 mb-0">{String(error)}</div>
            )}

            {loading ? (
                <div className="text-center py-3 text-muted small">
                    <Spinner animation="border" size="sm" /> Loading dashboard…
                </div>
            ) : (
                <div className="mt-2">
                    {/* grid: 3 колонки на десктопе, стэк на мобиле */}
                    <div className="row g-3">
                        {/* LEFT: Recent Activity (system-wide) */}
                        <div className="col-12 col-lg-4">
                            <Card className="h-100 border-secondary">
                                <Card.Header className="py-1 d-flex align-items-center justify-content-between">
                                    <span>Global Activity</span>
                                    <span
                                        className="activity-dot"
                                        aria-label={queueHasItems ? 'live activity' : 'idle'}
                                        title={queueHasItems ? 'Live' : 'Idle'}
                                        style={{ opacity: queueHasItems ? 1 : .35 }}
                                    />
                                </Card.Header>

                                <Card.Body className="p-2" aria-live="polite">
                                    {displayed?.length ? (
                                        <div className="mb-0 ms-1">
                                            {displayed.slice(0, 10).map((log, idx) => {
                                                const key = log.id ?? `${log._ts}-${idx}`;
                                                const isNew = (animatingId && (animatingId === (log.id ?? `${log._ts}-${log.text}`)));

                                                return (
                                                    <div
                                                        key={key}
                                                        className={`mb-1 me-2 ${isNew ? 'row-live pop-in' : ''}`}
                                                        style={{ borderRadius: 6, padding: isNew ? '2px 4px' : 0 }}
                                                    >
                                                        <div className="d-flex me-2 align-items-center justify-content-between">
                                                            <div className="d-flex align-items-center me-2" style={{ minWidth: 0 }}>

                                                                <div
                                                                    className={`small text-truncate ${isNew ? 'row-text' : ''}`}

                                                                    title={log.text || '—'}
                                                                >
                                                                    {log.text || '—'}
                                                                </div>

                                                            </div>
                                                            <div className="ms-2 small text-nowrap text-muted">
                                                                {fmtTimeAgo(log.createdAt)}
                                                            </div>

                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        </div>
                                    ) : (
                                        <div className="text-muted small">No recent activity</div>
                                    )}
                                </Card.Body>
                            </Card>

                        </div>

                        {/* RIGHT: My Activity */}
                        <div className="col-12 col-lg-4">
                            <Card className="h-100 border-secondary">
                                <Card.Header className="py-1">My Activity</Card.Header>
                                <Card.Body className="p-2">
                                    {meta?.authed ? (
                                        myLogs?.length ? (
                                            <div className="mb-0 ms-1">
                                                {myLogs.slice(0, 150).map((log, idx) => {
                                                    const text = shortMyText(log);
                                                    const createdAt = getCreatedAt(log);
                                                    return (
                                                        <div key={log.id || idx} className="mb-1 me-2">
                                                            <div className="d-flex me-2 align-items-center justify-content-between">
                                                                {/* короткая фраза без имени */}
                                                                <div className="me-2 small text-truncate" title={text}>
                                                                    {text}
                                                                </div>
                                                                {/* сколько времени назад */}
                                                                <div className="ms-2 small text-nowrap text-muted">
                                                                    {fmtTimeAgo(createdAt)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-muted small">No personal activity</div>
                                        )
                                    ) : (
                                        <div className="text-muted small">Sign in to see your activity</div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>

                        {/* MIDDLE: Top This Week */}
                        <div className="col-12 col-lg-4">
                            <Card className="h-100 border-secondary">
                                <div style={{ position: "relative" }}>
                                    <Card.Header className="py-1 d-flex justify-content-between align-items-center">
                                        <span>Weekly Leaderboard</span>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                            style={{ width: "20px", height: "20px", lineHeight: "1" }}
                                            onClick={() => setShowTooltip(!showTooltip)}
                                        >
                                           <strong>?</strong>
                                        </Button>
                                    </Card.Header>

                                    {showTooltip && (
                                        <div
                                            className="border border-secondary"
                                            style={{
                                                position: "absolute",
                                                top: "-210px",
                                                right: "0",
                                                background: "white",
                                                border: "1px solid #ccc",
                                                padding: "12px",
                                                borderRadius: "8px",
                                                zIndex: 100,
                                                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                                width: "320px",
                                            }}
                                        >
                                            <p style={{ fontSize: "14px", marginBottom: "10px" }}>
                                                <strong>Weekly Leaderboard</strong> <br />
                                                Shows the most active users over the last 7 days. Score = assignments published + assignments taken. Example: user takes 10 assignments and publishes 7 assignments for own book — score = 17, regardless of completion status of taken assignments.
                                            </p>
                                            <div className="d-flex justify-content-end">
                                                <button
                                                    onClick={() => setShowTooltip(false)}
                                                    className="btn btn-outline-success btn-sm"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>


                                <Card.Body className="p-2">
                                    {topWeek?.users?.length ? (
                                        <div className="mb-0 ms-1">
                                            {topWeek.users.map((row, idx) => {
                                                const placeClass = (() => {
                                                    if (idx === 0) return "bg-danger bg-opacity-100";
                                                    if (idx === 1) return "bg-success bg-opacity-75";
                                                    if (idx === 2) return "bg-success bg-opacity-50";
                                                    if (idx === 3) return "bg-warning text-dark";
                                                    if (idx === 4) return "bg-warning text-dark bg-opacity-50";
                                                    return "bg-info";
                                                })();

                                                const nameLine = `${row.tariff} User ${row.displayName}`;
                                                const myName = `You’re here ⭐ ➜`;

                                                return (
                                                    <div key={row.user?.id || idx} className="mb-0">
                                                        <div className="d-flex me-2 align-items-center justify-content-between">
                                                            {/* ЛЕВО: Premium User Poul / Free User Bs** */}
                                                            <div
                                                                className={`me-2 small text-truncate ${row.isSelf ? "fw-bolder text-dark" : ""}`}
                                                                title={row.isSelf ? myName : nameLine}
                                                                style={{ maxWidth: "70%" }}
                                                            >
                                                                {row.isSelf ? myName : nameLine}
                                                            </div>

                                                            {/* ПРАВО: бейдж места фиксированной ширины */}
                                                            <div className="ms-2">
                                                                 <span
                                                                     className={`badge ${placeClass} d-inline-flex align-items-center justify-content-between gap-1 text-center`}
                                                                     style={{ minWidth: 130 }}
                                                                 >
  <span>{row.badge}</span>
                                                                     {Number.isFinite(row.score) && (
                                                                         <small className="ms-1 opacity-75" style={{ fontSize: '0.8em' }}>+{row.score}
                                                                         </small>
                                                                     )}
</span>


                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-muted small">No leaders yet</div>
                                    )}
                                </Card.Body>


                            </Card>
                        </div>
                    </div>

                    {/* NEW ROW: 3 more leaderboards */}
                    <div className="row g-3 mt-0">
                        {/* 1) Best Review Quality (7d) */}
                        <div className="col-12 col-lg-4">
                            <Card className="h-100 border-secondary">
                                <div style={{ position: "relative" }}>
                                    <Card.Header className="py-1 d-flex justify-content-between align-items-center">
                                        <span>Best Review Quality Last 30 Days</span>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                            style={{ width: "20px", height: "20px", lineHeight: "1" }}
                                            onClick={() => setShowTooltipQuality(!showTooltipQuality)}
                                        >
                                            <strong>?</strong>
                                        </Button>
                                    </Card.Header>
                                    {showTooltipQuality && (
                                        <div
                                            className="border border-secondary"
                                            style={{
                                                position: "absolute",
                                                top: "-230px",
                                                right: "0",
                                                background: "white",
                                                border: "1px solid #ccc",
                                                padding: "12px",
                                                borderRadius: "8px",
                                                zIndex: 100,
                                                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                                width: "320px",
                                            }}
                                        >
                                            <p style={{ fontSize: "14px", marginBottom: "10px" }}>
                                                <strong>Best Review Quality Last 30 Days</strong><br/>
                                                Reviewer quality is calculated based on confirmed reviews and upheld complaints. If no complaints were filed, that’s excellent. This leaderboard highlights users who have posted more reviews while attracting no complaints about those reviews. If you see yourself here, you’re doing great!
                                            </p>
                                            <div className="d-flex justify-content-end">
                                                <button onClick={() => setShowTooltipQuality(false)} className="btn btn-outline-success btn-sm">
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <Card.Body className="p-2">
                                    {topQuality7d?.users?.length ? (
                                        <div className="mb-0 ms-1">
                                            {topQuality7d.users.map((row, idx) => {
                                                const placeClass = (() => {
                                                    if (idx === 0) return "bg-danger bg-opacity-100";
                                                    if (idx === 1) return "bg-success bg-opacity-75";
                                                    if (idx === 2) return "bg-success bg-opacity-50";
                                                    if (idx === 3) return "bg-warning text-dark";
                                                    if (idx === 4) return "bg-warning text-dark bg-opacity-50";
                                                    return "bg-info";
                                                })();
                                                const nameLine = `${row.tariff} User ${row.displayName}`;
                                                const myName = `You’re here ⭐ ➜`;
                                                return (
                                                    <div key={idx} className="mb-1">
                                                        <div className="d-flex me-2 align-items-center justify-content-between">
                                                            <div
                                                                className={`me-2 small text-truncate ${row.isSelf ? "fw-bolder text-dark" : ""}`}
                                                                title={row.isSelf ? myName : nameLine}
                                                                style={{ maxWidth: "80%" }}
                                                            >
                                                                {row.isSelf ? myName : nameLine}
                                                            </div>
                                                            <div className="ms-2 d-flex align-items-center gap-1">
                                                               <span
                                                                   className={`badge ${placeClass} d-inline-flex align-items-center justify-content-between gap-1 text-center`}
                                                                   style={{ minWidth: 140 }}
                                                               >
  <span>{row.badge}</span>
                                                                   {Number.isFinite(row.quality) && (
                                                                       <small className="ms-1 opacity-75" style={{ fontSize: '0.8em' }}>{row.total}/{row.quality}%</small>
                                                                   )}
</span>

                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-muted small">No leaders yet</div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>

                        {/* 2) Best Affiliates (7d) */}
                        <div className="col-12 col-lg-4">
                            <Card className="h-100 border-secondary">
                                <div style={{ position: "relative" }}>
                                    <Card.Header className="py-1 d-flex justify-content-between align-items-center">
                                        <span>Best Affiliates Last 30 Days</span>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                            style={{ width: "20px", height: "20px", lineHeight: "1" }}
                                            onClick={() => setShowTooltipAffiliate(!showTooltipAffiliate)}
                                        >
                                            <strong>?</strong>
                                        </Button>
                                    </Card.Header>
                                    {showTooltipAffiliate && (
                                        <div
                                            className="border border-secondary"
                                            style={{
                                                position: "absolute",
                                                top: "-230px",
                                                right: "0",
                                                background: "white",
                                                border: "1px solid #ccc",
                                                padding: "12px",
                                                borderRadius: "8px",
                                                zIndex: 100,
                                                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                                width: "320px",
                                            }}
                                        >
                                            <p style={{ fontSize: "14px", marginBottom: "10px" }}>
                                                <strong>Best Affiliates Last 30 Days</strong><br/>
                                                Highlights users who generated the most direct referrals in the last 30 days.
                                                Want to appear here? Join our affiliate program: invite new authors and reviewers with your referral link,
                                                help them get started, and earn ongoing rewards — a straightforward way to build
                                                an additional income stream for years ahead.
                                            </p>
                                            <div className="d-flex justify-content-end">
                                                <button onClick={() => setShowTooltipAffiliate(false)} className="btn btn-outline-success btn-sm">
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <Card.Body className="p-2">
                                    {topAffiliate7d?.users?.length ? (
                                        <div className="mb-0 ms-1">
                                            {topAffiliate7d.users.map((row, idx) => {
                                                const placeClass = (() => {
                                                    if (idx === 0) return "bg-danger bg-opacity-100";
                                                    if (idx === 1) return "bg-success bg-opacity-75";
                                                    if (idx === 2) return "bg-success bg-opacity-50";
                                                    if (idx === 3) return "bg-warning text-dark";
                                                    if (idx === 4) return "bg-warning text-dark bg-opacity-50";
                                                    return "bg-info";
                                                })();
                                                const nameLine = `${row.tariff} User ${row.displayName}`;
                                                const myName = `You’re here ⭐ ➜`;
                                                return (
                                                    <div key={idx} className="mb-1">
                                                        <div className="d-flex me-2 align-items-center justify-content-between">
                                                            <div
                                                                className={`me-2 small text-truncate ${row.isSelf ? "fw-bolder text-dark" : ""}`}
                                                                title={row.isSelf ? myName : nameLine}
                                                                style={{ maxWidth: "70%" }}
                                                            >
                                                                {row.isSelf ? myName : nameLine}
                                                            </div>
                                                            <div className="ms-2 d-flex align-items-center gap-1">
                                                                  <span
                                                                      className={`badge ${placeClass} d-inline-flex align-items-center justify-content-between gap-1 text-center`}
                                                                      style={{ minWidth: 130 }}
                                                                  >
  <span>{row.badge}</span>
                                                                      {Number.isFinite(row.referrals) && (
                                                                          <small className="ms-1 opacity-75" style={{ fontSize: '0.8em' }}>+{row.referrals}
                                                                          </small>
                                                                      )}
</span>

                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-muted small">No leaders yet</div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>

                        {/* 3) Burn Notice (7d) */}
                        <div className="col-12 col-lg-4">
                            <Card className="h-100 border-secondary">
                                <div style={{ position: "relative" }}>
                                    <Card.Header className="py-1 d-flex justify-content-between align-items-center">
                                        <span>Golden Raspberry Last 30 Days</span>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                            style={{ width: "20px", height: "20px", lineHeight: "1" }}
                                            onClick={() => setShowTooltipBurn(!showTooltipBurn)}
                                        >
                                            <strong>?</strong>
                                        </Button>
                                    </Card.Header>
                                    {showTooltipBurn && (
                                        <div
                                            className="border border-secondary"
                                            style={{
                                                position: "absolute",
                                                top: "-380px",
                                                right: "0",
                                                background: "white",
                                                border: "1px solid #ccc",
                                                padding: "12px",
                                                borderRadius: "8px",
                                                zIndex: 100,
                                                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                                width: "320px",
                                            }}
                                        >
                                            <p style={{ fontSize: "14px", marginBottom: "10px" }}>
                                                <strong>Burn Notice Last 30 Days</strong><br/>
                                                We hope everyone stays off this list. This is an anti-leaderboard of users with the most
                                                complaint-based refunds in the last 30 days. If your name shows up here, it’s a sign to change
                                                your Amazon review strategy.
                                                <br/>
                                                Most complaints happen because reviews disappear. That usually means posting many Non-Verified
                                                Purchase reviews (reviews without buying the book). This doesn’t work. Amazon notices it,
                                                and your results get worse.
                                                <br/>
                                                On Virtalibry, this behavior isn’t welcome. We detect it, limit it, and keep making it harder
                                                to game the system. Aim for zero complaints — and never see your name here.
                                            </p>

                                            <div className="d-flex justify-content-end">
                                                <button onClick={() => setShowTooltipBurn(false)} className="btn btn-outline-success btn-sm">
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <Card.Body className="p-2">
                                    {topBurn7d?.users?.length ? (
                                        <div className="mb-0 ms-1">
                                            {topBurn7d.users.map((row, idx) => {
                                                const placeClass = (() => {
                                                    if (idx === 0) return "bg-danger bg-opacity-100";
                                                    if (idx === 1) return "bg-success bg-opacity-75";
                                                    if (idx === 2) return "bg-success bg-opacity-50";
                                                    if (idx === 3) return "bg-warning text-dark";
                                                    if (idx === 4) return "bg-warning text-dark bg-opacity-50";
                                                    return "bg-info";
                                                })();
                                                const nameLine = `${row.tariff} User ${row.displayName}`;
                                                const myName = `You’re here ⭐ ➜`;
                                                return (
                                                    <div key={idx} className="mb-1">
                                                        <div className="d-flex me-2 align-items-center justify-content-between">
                                                            <div
                                                                className={`me-2 small text-truncate ${row.isSelf ? "fw-bolder text-dark" : ""}`}
                                                                title={row.isSelf ? myName : nameLine}
                                                                style={{ maxWidth: "70%" }}
                                                            >
                                                                {row.isSelf ? myName : nameLine}
                                                            </div>
                                                            <div className="ms-2 d-flex align-items-center gap-1">
                                                                <span
                                                                    className={`badge ${placeClass} d-inline-flex align-items-center justify-content-between gap-1 text-center`}
                                                                    style={{ minWidth: 130 }}
                                                                >
                                                                    <span>{row.badge}</span>
                                                                    {Number.isFinite(row.complaints) && (
                                                                        <small className="ms-1 opacity-75" style={{ fontSize: '0.8em' }}>-{row.complaints}
                                                                        </small>
                                                                    )}
                                                                </span>

                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-muted small">No leaders yet</div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                </div>
            )}
        </Card>
        </>
    );
};

export default DashboardActivity;
